/**
 * Created: 2026-10-15
 * Initial commit: Admin schedules coaching appointments from the slots page
 *
 * Coaching appointment rules shared by the mini coach appoint flow and the
 * web admin slots page, so both offer what `createCoachingAppointment` accepts.
 *
 * Updated: 2026-10-19 - A selection may mix courts of different sports: a
 * tennis course for a child or beginner can be taught on a pickleball court
 * Updated: 2026-11-03 - Open enrollment is for courses of at least 3 seats
 * Updated: 2026-11-03 - Whether a player's entitlements cover an enrollment
 */

/** Fewest seats a course needs to be open for enrollment: a group class. */
export const ENROLL_MIN_SEATS = 3

/** Whether a course of `seats` may be scheduled in ENROLL roster mode. */
export function isEnrollable(seats: number) {
  return seats >= ENROLL_MIN_SEATS
}

/** The parts of a course entitlement that decide whether it can pay for a class. */
export interface EnrollEntitlement {
  totalHours: number
  consumedHours: number
  /** yyyy-MM-dd, usable through the end of that day. */
  validUntil: string
}

/**
 * Whether `entitlements` (the player's ACTIVE ones for the course) hold the
 * `hours` an enrollment deducts, counting only those valid through
 * `lastDate` (yyyy-MM-dd), as `enrollCoachingAppointment` does. The hours
 * may come from several entitlements. Hours held by the player's unconfirmed
 * INVITE appointments are not known here, so the mutation may still refuse.
 */
export function hasEnrollHours(
  entitlements: EnrollEntitlement[],
  hours: number,
  lastDate: string,
) {
  const available = entitlements
    .filter((e) => e.validUntil >= lastDate)
    .reduce((sum, e) => sum + e.totalHours - e.consumedHours, 0)
  return available >= hours
}

/** One person may bring at most one companion (e.g. a parent with a child). */
export const MAX_HEADCOUNT = 2

/** One court-hour of an appointment selection. */
export interface CoachSelectedSlot {
  courtId: string
  hour: number
}

/**
 * Whether `slots` may be scheduled as one coaching appointment, mirroring
 * `createCoachingAppointment`'s checks: at least one slot, one court per hour,
 * and the hours consecutive. Courts may differ
 * from hour to hour. The date is not checked: the picker shows one date.
 */
export function isValidCoachSelection(slots: CoachSelectedSlot[]) {
  if (slots.length === 0) return false
  const hours = slots.map((s) => s.hour)
  if (new Set(hours).size !== hours.length) return false
  return Math.max(...hours) - Math.min(...hours) + 1 === hours.length
}

/** Heads a student's available hours pay for: each head draws `sessionHours`. */
export function hoursCapOf(availableHours: number, sessionHours: number) {
  return Math.floor(availableHours / Math.max(sessionHours, 1))
}

/**
 * Most people one invited student may bring, themselves included: capped by
 * `MAX_HEADCOUNT`, by the seats left once the others' headcounts are taken,
 * and by the student's hours. Never below 1.
 */
export function maxHeadcountOf({
  seats,
  totalHeadcount,
  ownHeadcount,
  availableHours,
  sessionHours,
}: {
  seats: number
  totalHeadcount: number
  ownHeadcount: number
  availableHours: number
  sessionHours: number
}) {
  return Math.max(
    1,
    Math.min(
      MAX_HEADCOUNT,
      seats - (totalHeadcount - ownHeadcount),
      hoursCapOf(availableHours, sessionHours),
    ),
  )
}
